// Analytics utility functions for production tracking

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
}

export const trackEvent = (event: string, properties?: Record<string, any>) => {
  // Microsoft Clarity custom events
  if (typeof window !== 'undefined' && (window as any).clarity) {
    (window as any).clarity('set', event, properties);
  }

  // Console log for development
  if (import.meta.env.DEV) {
    console.log('Analytics Event:', { event, properties });
  }
};

export const trackFormSubmission = (formType: 'main-form' | 'sticky-bar', email: string, additionalData?: Record<string, any>) => {
  trackEvent('form_submission', {
    form_type: formType,
    email_domain: email.split('@')[1] || 'unknown',
    timestamp: new Date().toISOString(),
    ...additionalData
  });
};

export const trackUserInteraction = (interaction: string, element?: string, value?: string) => {
  trackEvent('user_interaction', {
    interaction,
    element,
    value,
    timestamp: new Date().toISOString(),
    page: window.location.pathname
  });
};

export const trackPageView = (page: string, additionalData?: Record<string, any>) => {
  trackEvent('page_view', {
    page,
    url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    ...additionalData
  });
};

export const trackConversion = (type: 'waitlist_join' | 'email_signup', value?: number) => {
  trackEvent('conversion', {
    type,
    value: value || 1,
    timestamp: new Date().toISOString(),
    page: window.location.pathname
  });
};

export const trackError = (error: string, context?: string, additionalData?: Record<string, any>) => {
  trackEvent('error', {
    error,
    context,
    timestamp: new Date().toISOString(),
    page: window.location.pathname,
    user_agent: navigator.userAgent,
    ...additionalData
  });
};

export const trackPerformance = (metric: string, value: number, unit: string = 'ms') => {
  trackEvent('performance', {
    metric,
    value,
    unit,
    timestamp: new Date().toISOString(),
    page: window.location.pathname
  });
};

export const initializeAnalytics = () => {
  // Track page load time
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      const loadTime = performance.now();
      trackPerformance('page_load_time', Math.round(loadTime));
    });

    // Track time on page when user leaves
    let startTime = Date.now();
    
    const trackTimeOnPage = () => {
      const timeOnPage = Math.round((Date.now() - startTime) / 1000);
      trackEvent('time_on_page', {
        duration: timeOnPage,
        page: window.location.pathname
      });
    };

    window.addEventListener('beforeunload', trackTimeOnPage);
    window.addEventListener('pagehide', trackTimeOnPage);

    // Track scroll depth
    let maxScrollPercent = 0;
    const trackScrollDepth = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScrollPercent) {
        maxScrollPercent = scrollPercent;
        
        // Track milestone scroll depths
        const milestones = [25, 50, 75, 90, 100];
        const milestone = milestones.find(m => scrollPercent >= m && maxScrollPercent < m);
        
        if (milestone) {
          trackEvent('scroll_depth', {
            percent: milestone,
            page: window.location.pathname
          });
        }
      }
    };

    let scrollTimeout: NodeJS.Timeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(trackScrollDepth, 100);
    });
  }
};

export const getEngagementScore = (): number => {
  // Calculate engagement score based on various factors
  const factors = {
    timeOnPage: Math.min((Date.now() - performance.timeOrigin) / 60000, 10), // Max 10 points for 10+ minutes
    scrollDepth: maxScrollPercent / 10, // Max 10 points for 100% scroll
    interactions: Math.min(getInteractionCount(), 5) // Max 5 points for 5+ interactions
  };

  return Math.round(factors.timeOnPage + factors.scrollDepth + factors.interactions);
};

let interactionCount = 0;
export const incrementInteractionCount = () => {
  interactionCount++;
};

export const getInteractionCount = () => {
  return interactionCount;
};

// Track clicks on important elements
export const setupClickTracking = () => {
  if (typeof window === 'undefined') return;

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    
    // Track button clicks
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      const button = target.tagName === 'BUTTON' ? target : target.closest('button');
      const text = button?.textContent?.trim();
      trackUserInteraction('button_click', 'button', text);
      incrementInteractionCount();
    }

    // Track link clicks
    if (target.tagName === 'A' || target.closest('a')) {
      const link = target.tagName === 'A' ? target : target.closest('a');
      const href = link?.getAttribute('href');
      const text = link?.textContent?.trim();
      trackUserInteraction('link_click', 'link', `${text} -> ${href}`);
      incrementInteractionCount();
    }

    // Track form interactions
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      const inputType = target.getAttribute('type') || target.tagName.toLowerCase();
      trackUserInteraction('form_interaction', inputType, target.getAttribute('name') || 'unnamed');
      incrementInteractionCount();
    }
  });
};

export const maxScrollPercent = 0; // Export for scroll tracking